import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Spinner, Badge } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import axios from 'axios';

const ProgramaManagement = () => {
  const { hasPermission } = useAuth();
  const [socios, setSocios] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [valores, setValores] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [filterMes, setFilterMes] = useState(new Date().getMonth() + 1);
  const [filterAño, setFilterAño] = useState(new Date().getFullYear());

  const puedeEditar = hasPermission('socios', 'update');

  const fetchSocios = useCallback(async () => {
    try {
      const response = await axios.get('/api/users?role=socio&limit=1000');
      setSocios(response.data.users || []);
    } catch (error) {
      console.error('Error fetching socios:', error);
      toast.error('Error al cargar socios');
    }
  }, []);

  const fetchRegistros = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/registros-mensuales?mes=${filterMes}&año=${filterAño}`);
      const data = response.data.data || [];
      setRegistros(data);

      const mapaValores = {};
      data.forEach(r => {
        mapaValores[r.socio._id] = r.programa || 0;
      });
      setValores(mapaValores);
    } catch (error) {
      console.error('Error fetching registros:', error);
      toast.error('Error al cargar los registros de programa');
    } finally {
      setLoading(false);
    }
  }, [filterMes, filterAño]);

  useEffect(() => {
    fetchSocios();
  }, [fetchSocios]);

  useEffect(() => {
    fetchRegistros();
  }, [fetchRegistros]);

  const handleValorChange = (socioId, value) => {
    setValores(prev => ({
      ...prev,
      [socioId]: value === '' ? '' : value
    }));
  };

  const handleGuardar = async (socio) => {
    if (!puedeEditar) {
      toast.error('No tienes permisos para registrar el programa');
      return;
    }

    const valorRaw = valores[socio._id];
    const valor = valorRaw === '' || valorRaw === undefined ? 0 : parseFloat(valorRaw);

    if (isNaN(valor) || valor < 0) {
      toast.error('Ingresa un valor válido');
      return;
    }

    setSavingId(socio._id);
    try {
      const registroExistente = registros.find(r => r.socio._id === socio._id);

      if (registroExistente) {
        await axios.put(`/api/registros-mensuales/${registroExistente._id}`, {
          programa: valor
        });
      } else {
        await axios.post('/api/registros-mensuales', {
          socio: socio._id,
          mes: filterMes,
          año: filterAño,
          programa: valor
        });
      }

      toast.success(`Programa de ${socio.name} actualizado`);
      await fetchRegistros();
    } catch (error) {
      console.error('Error al guardar programa:', error);
      toast.error(error.response?.data?.message || 'Error al guardar el programa');
    } finally {
      setSavingId(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getNombreMes = (mes) => {
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                   'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return meses[mes - 1];
  };

  const totalPrograma = registros.reduce((sum, r) => sum + (r.programa || 0), 0);

  if (loading && socios.length === 0) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <div className="fade-in">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <Card className="card-custom">
              <Card.Body>
                <h2 className="text-gradient mb-2">
                  <i className="fas fa-star me-2"></i>
                  Gestión de Programa
                </h2>
                <p className="text-muted mb-0">
                  Registra el valor del programa de cada socio, mes a mes
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filtros de Mes y Año */}
        <Row className="mb-4">
          <Col md={6}>
            <Card className="card-custom">
              <Card.Body>
                <Form.Group>
                  <Form.Label>
                    <i className="fas fa-calendar me-2"></i>
                    Mes
                  </Form.Label>
                  <Form.Select value={filterMes} onChange={(e) => setFilterMes(parseInt(e.target.value))}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(mes => (
                      <option key={mes} value={mes}>
                        {getNombreMes(mes)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="card-custom">
              <Card.Body>
                <Form.Group>
                  <Form.Label>
                    <i className="fas fa-calendar me-2"></i>
                    Año
                  </Form.Label>
                  <Form.Select value={filterAño} onChange={(e) => setFilterAño(parseInt(e.target.value))}>
                    {Array.from({ length: 16 }, (_, i) => 2020 + i).map(año => (
                      <option key={año} value={año}>{año}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Total del mes */}
        <Row className="mb-4">
          <Col md={4}>
            <Card className="stats-card border-primary">
              <Card.Body>
                <div className="stats-icon text-primary">
                  <i className="fas fa-star"></i>
                </div>
                <div className="stats-number text-primary">
                  {formatCurrency(totalPrograma)}
                </div>
                <div className="stats-label">Total Programa - {getNombreMes(filterMes)} {filterAño}</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabla de Socios */}
        <Row>
          <Col>
            <Card className="card-custom">
              <Card.Header className="card-header-custom">
                <h5 className="mb-0">
                  <i className="fas fa-list me-2"></i>
                  Programa de {getNombreMes(filterMes)} {filterAño}
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th>Socio</th>
                        <th>Cédula</th>
                        <th>Número Socio</th>
                        <th style={{ width: '220px' }}>Programa ($)</th>
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {socios.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center text-muted py-4">
                            <i className="fas fa-inbox fa-3x mb-3 d-block"></i>
                            No hay socios registrados
                          </td>
                        </tr>
                      ) : (
                        socios.map(socio => (
                          <tr key={socio._id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <i className="fas fa-user-circle fa-2x text-primary me-2"></i>
                                <div>
                                  <div className="fw-bold">{socio.name}</div>
                                  <small className="text-muted">{socio.email}</small>
                                </div>
                              </div>
                            </td>
                            <td>{socio.cedula}</td>
                            <td>
                              <Badge bg="info">{socio.socioInfo?.numeroSocio || 'N/A'}</Badge>
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                min="0"
                                step="0.01"
                                value={valores[socio._id] ?? 0}
                                onChange={(e) => handleValorChange(socio._id, e.target.value)}
                                disabled={!puedeEditar}
                              />
                            </td>
                            <td className="text-center">
                              {puedeEditar && (
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleGuardar(socio)}
                                  disabled={savingId === socio._id}
                                >
                                  {savingId === socio._id ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : (
                                    <>
                                      <i className="fas fa-save me-1"></i>
                                      Guardar
                                    </>
                                  )}
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default ProgramaManagement;
